import type { SVGProps } from 'react';

/**
 * ExpandAllIcon — vendored Octicon "unfold" glyph, 16px (ADR B6a).
 *
 * The column heading bar's Expand-all / Collapse-all pair (ADR B6) is
 * required to use Treeview's icons — `UnfoldIcon` / `FoldIcon` from
 * `@primer/octicons-react`
 * (`packages/treeview/src/components/filterable-tree/TreeWithDetailsView.tsx:3,746-767`).
 * `@primer/octicons-react` is not a dependency of `packages/mm-web` — mm-web
 * declares only `@heroicons/react` (`CLAUDE.md` "Icons: Heroicons",
 * `DESIGN.md:3`'s Catalyst/Heroicons sourcing rule) — and treeview (React 19
 * + Primer + styled-components) cannot be imported from mm-web (React 18 +
 * Tailwind/Catalyst): the two packages have separate installs and no
 * workspace hoisting. So this vendors the glyph's path data verbatim behind
 * the same wrapper shape `components_misc/CollapseIcon.tsx` and
 * `ExpandIcon.tsx` already use — a semantic name in front of a concrete
 * glyph, `aria-hidden` defaulted to `true`, `className` and the rest of
 * `SVGProps<SVGSVGElement>` passed through — rather than adding the
 * dependency or importing across packages. This is a documented exception to
 * the Heroicons-only policy (`docs/current/DESIGN.md`), flagged for
 * design-lead review on this task's PR.
 *
 * Source: `@primer/octicons-react@19.15.2` (the version `packages/treeview`
 * pins), glyph name `unfold`, 16px variant, path data verbatim. MIT licensed
 * (Copyright (c) GitHub Inc., https://github.com/primer/octicons).
 */
export function ExpandAllIcon({
  className = '',
  'aria-hidden': ariaHidden = true,
  ...props
}: SVGProps<SVGSVGElement>) {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      viewBox="0 0 16 16"
      fill="currentColor"
      className={className}
      aria-hidden={ariaHidden}
      {...props}
    >
      <path d="m8.177.677 2.896 2.896a.25.25 0 0 1-.177.427H8.75v1.25a.75.75 0 0 1-1.5 0V4H5.104a.25.25 0 0 1-.177-.427L7.823.677a.25.25 0 0 1 .354 0ZM7.25 10.75a.75.75 0 0 1 1.5 0V12h2.146a.25.25 0 0 1 .177.427l-2.896 2.896a.25.25 0 0 1-.354 0l-2.896-2.896A.25.25 0 0 1 5.104 12H7.25v-1.25Zm-5-2a.75.75 0 0 0 0-1.5h-.5a.75.75 0 0 0 0 1.5h.5ZM6 8a.75.75 0 0 1-.75.75h-.5a.75.75 0 0 1 0-1.5h.5A.75.75 0 0 1 6 8Zm2.25.75a.75.75 0 0 0 0-1.5h-.5a.75.75 0 0 0 0 1.5h.5ZM12 8a.75.75 0 0 1-.75.75h-.5a.75.75 0 0 1 0-1.5h.5A.75.75 0 0 1 12 8Zm2.25.75a.75.75 0 0 0 0-1.5h-.5a.75.75 0 0 0 0 1.5h.5Z" />
    </svg>
  );
}
